import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAll } from '../lib/fetchall'
import { useRoster } from '../lib/roster'
import { GAP_VIEWS, TAB_PATH, describeView } from '../lib/views'

/**
 * What's missing.
 *
 * Every one of these was already findable and none of them was visible: you
 * could only see the agents with no address by knowing to set two dropdowns
 * that nobody would think to combine. A gap you cannot see is a gap that never
 * gets filled.
 *
 * The counts come from the live roster, so the list goes down as Renee works
 * it — which is the difference between a worklist and a report.
 */
export default function Gaps() {
  const r = useRoster()
  const navigate = useNavigate()

  /**
   * Walkthrough-flagged 2026-08-21. Not a `View` like everything in
   * `GAP_VIEWS` below — a bounce lives in its own table (`bounces`), not a
   * column on `agents`, so there is no roster filter that could express it
   * the way `applyView` does for every other row here. Counted separately
   * (distinct `agent_id` across all bounces) and routed straight to the
   * Bounces tab instead of through the roster.
   */
  const [bounceCount, setBounceCount] = useState<number | null>(null)
  const [bounceError, setBounceError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      // `fetchAll`, not a plain `.select` — a distinct-agent count over the
      // WHOLE table, and PostgREST silently caps an unpaginated read at 1000
      // rows (see `lib/fetchall.ts`). `id` carries the query's required
      // unique order tail; `agent_id` is what the count actually needs.
      const { rows, error } = await fetchAll<{ agent_id: string }>('bounces', 'agent_id,id', ['id'])
      if (cancelled) return
      if (error) {
        setBounceError(error)
        return
      }
      setBounceCount(new Set(rows.map((row) => row.agent_id)).size)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (r.error) return <div className="empty">Could not load the roster: {r.error}</div>
  if (!r.agents) return <div className="empty">Loading roster…</div>

  // Active agents only — every count below excludes departed, and a headline
  // that counts them says the roster is bigger than the rows can account for.
  const total = r.agents.filter((a) => a.is_active).length

  return (
    <div className="gaps">
      <h2>What's missing</h2>
      <p className="hint">
        Where the roster has holes, and where something looks worth a second look. Nothing here is
        broken — it is what {total} agents' worth of records collected from two systems actually
        looks like. Open any of them to see who, and Export to get the list.
      </p>

      {GAP_VIEWS.map(({ view, note }) => {
        const n = r.countFor(view)
        return (
          <div className={`gap ${n === 0 ? 'clear' : ''}`} key={view.label}>
            <div className="gap-n">{n}</div>
            <div className="gap-body">
              <strong>{view.label}</strong>
              {note && <div className="hint">{note}</div>}
              <div className="hint dim">{describeView(view)}</div>
            </div>
            <button
              className="btn sm"
              disabled={n === 0}
              onClick={() => {
                r.applyView(view)
                navigate(TAB_PATH[view.tab] ?? '/')
              }}
            >
              {n === 0 ? 'None' : 'Show me'}
            </button>
          </div>
        )
      })}

      <div className={`gap ${bounceCount === 0 ? 'clear' : ''}`}>
        <div className="gap-n">{bounceCount ?? '…'}</div>
        <div className="gap-body">
          <strong>{bounceCount === 1 ? '1 agent has' : `${bounceCount ?? 0} agents have`} an email address that bounced</strong>
          {bounceError && <div className="hint">Could not load: {bounceError}</div>}
          <div className="hint dim">
            Reported automatically off the Bulk lane, or by hand from the Reviewed lane. Its own
            table, not a roster filter — includes departed agents, unlike every count above.
          </div>
        </div>
        <button className="btn sm" disabled={!bounceCount} onClick={() => navigate('/bounces')}>
          {bounceCount ? 'Show me' : 'None'}
        </button>
      </div>

      <p className="hint">
        These counts follow the roster, so they go down as the gaps get filled. Departed agents are
        not counted, except in the bounces row above.
      </p>
    </div>
  )
}
